// components/OpenDayBody.jsx
import React from 'react';
import SearchAndTopicBar from './SearchAndTopicBar';
import EventTeaser from './EventTeaser';
import Pagination from './Pagination';

const PAGE_SIZE = 25;

const OpenDayBody = ({
  item,
  events = [],
  topics = [],
  searchValue = '',
  selectedTopicId = 'all',
  currentPage = 1,
  onSearchChange,
  onTopicFilterChange,
  onPageChange,
  isLoadingEvents,
  isErrorEvents,
  lang = 'en',
}) => {
  const searchTerm = (searchValue || '').toLowerCase();

  // 1) Start from all events
  let filteredEvents = events;

  // 2) Search filter
  if (searchTerm) {
    filteredEvents = filteredEvents.filter(ev => {
      const topicName = ev.__topicName || '';
      const title = ev.title || '';
      const descShort = ev.description_short || '';
      const desc = ev.description || '';
      const locationTitle = ev.location?.title || '';
      const typeLabel = ev.programType?.type || '';
      const schoolName = ev.school?.name || '';
      const room = ev.room || '';
      const floor = ev.floor || '';

      const haystack = [
        topicName,
        title,
        descShort,
        desc,
        locationTitle,
        typeLabel,
        schoolName,
        room,
        floor,
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(searchTerm);
    });
  }

  // 3) Topic filter (after search, before sort/pagination)
  if (selectedTopicId && selectedTopicId !== 'all') {
    filteredEvents = filteredEvents.filter(
      (ev) => String(ev.__topicId) === String(selectedTopicId)
    );
  }

  // 4) Sort by start time, then end time, then title
  filteredEvents = [...filteredEvents].sort((a, b) => {
    const aTime = new Date(a.start_time).getTime();
    const bTime = new Date(b.start_time).getTime();
    if (aTime !== bTime) return aTime - bTime;

    const aEndTime = new Date(a.end_time).getTime();
    const bEndTime = new Date(b.end_time).getTime();
    if (aEndTime !== bEndTime) return aEndTime - bEndTime;

    const aTitle = (a.title || '').toLowerCase();
    const bTitle = (b.title || '').toLowerCase();
    return aTitle.localeCompare(bTitle);
  });

  // 5) Pagination
  const totalPages = Math.max(
    1,
    Math.ceil(filteredEvents.length / PAGE_SIZE)
  );

  const safePage = Math.min(
    Math.max(currentPage, 1),
    totalPages
  );

  const startIndex = (safePage - 1) * PAGE_SIZE;
  const pageEvents = filteredEvents.slice(
    startIndex,
    startIndex + PAGE_SIZE
  );

  return (
    <>
      <div id={`search-filter-${item.id}`}>
        <SearchAndTopicBar
          itemId={item.id}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          topics={topics}
          selectedTopicId={selectedTopicId}
          onTopicFilterChange={onTopicFilterChange}
          lang={lang}
        />
      </div>

      {/* Loading / error states */}
      {isLoadingEvents && <p>Loading events…</p>}

      {isErrorEvents && (
        <p style={{ color: 'red' }}>
          There was a problem loading the events.
        </p>
      )}

      {/* Event list */}
      {pageEvents.length > 0 && (
        <div className="programme-event-list">
          {pageEvents.map((program, index) => (
            <EventTeaser 
              key={program.id} 
              program={program} 
              lang={lang}
              isLast={index === pageEvents.length - 1}
            />
          ))}
        </div>
      )}

      {pageEvents.length === 0 &&
        !isLoadingEvents &&
        events.length > 0 && (
          <p>No events match your filters.</p>
        )}

      {/* Pagination - SIMPLIFIED, RELYING ON Pagination.jsx TO RENDER ONLY IF totalPages > 1 */}
      <Pagination
        totalPages={totalPages}
        currentPage={safePage}
        onPageChange={onPageChange}
        scrollTargetId={`search-filter-${item.id}`}
      />
    </>
  );
};

export default OpenDayBody;
